import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useSeoMeta } from '@/hooks/useSeoMeta';
import { Link } from 'react-router-dom';

const BASE_URL = import.meta.env.VITE_SITE_URL ?? 'https://roameurope.blog';

const schemaJson = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Privacy Policy — roameurope.blog',
  description: 'Read the Privacy Policy for roameurope.blog, including how we use cookies, Google AdSense, and your rights under GDPR and CCPA.',
  url: `${BASE_URL}/privacy-policy`,
  dateModified: '2026-04-09',
  publisher: {
    '@type': 'Organization',
    name: 'roameurope.blog',
    url: BASE_URL,
  },
  inLanguage: 'en-US',
});

export default function PrivacyPage() {
  useSeoMeta({
    title: 'Privacy Policy — roameurope.blog',
    description: 'Read the Privacy Policy for roameurope.blog, including how we use cookies, Google AdSense, and your rights under GDPR and CCPA.',
    url: '/privacy-policy',
    type: 'website',
    noindex: true,
  });

  const lastUpdated = 'April 9, 2026';

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: schemaJson }}
      />
      <Navbar />
      <main className="pt-28 pb-20 min-h-screen bg-white dark:bg-stone-950 transition-colors duration-300">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <h1 className="text-4xl font-bold text-stone-900 dark:text-stone-50 mb-2" style={{ fontFamily: "'Lora', serif" }}>
            Privacy Policy
          </h1>
          <p className="text-xs text-stone-400 dark:text-stone-500 mb-10">Last updated: {lastUpdated}</p>

          <div className="space-y-8 text-stone-600 dark:text-stone-400 leading-relaxed text-sm">

            <section>
              <h2 className="text-lg font-bold text-stone-800 dark:text-stone-200 mb-2">1. Introduction</h2>
              <p>roameurope.blog (&ldquo;we&rdquo;, &ldquo;us&rdquo;, or &ldquo;our&rdquo;) is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and share information when you visit our website at roameurope.blog (the &ldquo;Site&rdquo;). By using the Site, you agree to the collection and use of information in accordance with this policy.</p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-stone-800 dark:text-stone-200 mb-2">2. Information We Collect</h2>
              <p className="mb-3">We do not directly collect personal information from visitors. However, the following data may be collected automatically or by third-party services:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>IP address and approximate geographic location</li>
                <li>Browser type, version, and operating system</li>
                <li>Pages visited, time spent, and referring URLs</li>
                <li>Cookie identifiers and device identifiers</li>
                <li>Email address (only if you voluntarily subscribe to our newsletter)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-bold text-stone-800 dark:text-stone-200 mb-2">3. Google AdSense &amp; Advertising</h2>
              <p className="mb-3">We use <strong>Google AdSense</strong> to display advertisements on this Site. Google and its partners use cookies and similar technologies to serve ads based on your prior visits to this and other websites.</p>
              <ul className="list-disc pl-5 space-y-1 mb-3">
                <li>Google&apos;s use of advertising cookies enables it and its partners to serve ads based on your visit to our site and/or other sites on the Internet.</li>
                <li>You may opt out of personalised advertising by visiting <a href="https://www.google.com/settings/ads" rel="nofollow noopener" target="_blank" className="text-rose-500 hover:underline">Google Ads Settings</a>.</li>
                <li>You may also opt out via the <a href="https://optout.networkadvertising.org/" rel="nofollow noopener" target="_blank" className="text-rose-500 hover:underline">Network Advertising Initiative opt-out page</a>.</li>
              </ul>
              <p>For more information on how Google uses data, visit <a href="https://policies.google.com/technologies/partner-sites" rel="nofollow noopener" target="_blank" className="text-rose-500 hover:underline">How Google uses data when you use our partners&apos; sites or apps</a>.</p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-stone-800 dark:text-stone-200 mb-2">4. Cookies</h2>
              <p className="mb-3">We use cookies for the following purposes:</p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="bg-stone-50 dark:bg-stone-800">
                      <th className="text-left px-3 py-2 border border-stone-200 dark:border-stone-700 text-stone-700 dark:text-stone-300 font-semibold">Category</th>
                      <th className="text-left px-3 py-2 border border-stone-200 dark:border-stone-700 text-stone-700 dark:text-stone-300 font-semibold">Purpose</th>
                      <th className="text-left px-3 py-2 border border-stone-200 dark:border-stone-700 text-stone-700 dark:text-stone-300 font-semibold">Provider</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['Essential', 'Theme preference, cookie consent state', 'roameurope.blog'],
                      ['Analytics', 'Understanding site usage and traffic patterns', 'Google Analytics'],
                      ['Advertising', 'Personalised ads based on browsing behaviour', 'Google AdSense'],
                    ].map(([cat, purpose, provider]) => (
                      <tr key={cat} className="border-b border-stone-100 dark:border-stone-800">
                        <td className="px-3 py-2 border border-stone-200 dark:border-stone-700 font-medium text-stone-700 dark:text-stone-300">{cat}</td>
                        <td className="px-3 py-2 border border-stone-200 dark:border-stone-700">{purpose}</td>
                        <td className="px-3 py-2 border border-stone-200 dark:border-stone-700">{provider}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-3">You can manage your cookie preferences at any time using our cookie consent banner or through your browser settings.</p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-stone-800 dark:text-stone-200 mb-2">5. How We Use Your Information</h2>
              <ul className="list-disc pl-5 space-y-1">
                <li>To display relevant advertisements via Google AdSense</li>
                <li>To analyse site traffic and improve user experience</li>
                <li>To send newsletter emails (only if you have subscribed)</li>
                <li>To comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-bold text-stone-800 dark:text-stone-200 mb-2">6. Data Retention</h2>
              <p>We retain personal data only for as long as necessary to fulfil the purposes outlined in this policy. Newsletter subscriber data is retained until you unsubscribe. Analytics data is retained in accordance with Google&apos;s data retention policies (typically 26 months). Cookie consent records are retained for 12 months.</p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-stone-800 dark:text-stone-200 mb-2">7. Your Rights (GDPR &amp; CCPA)</h2>
              <p className="mb-3">Depending on your location, you may have the following rights regarding your personal data:</p>
              <ul className="list-disc pl-5 space-y-1 mb-3">
                <li><strong className="text-stone-700 dark:text-stone-300">Right to access</strong> — Request a copy of the personal data we hold about you</li>
                <li><strong className="text-stone-700 dark:text-stone-300">Right to rectification</strong> — Request correction of inaccurate data</li>
                <li><strong className="text-stone-700 dark:text-stone-300">Right to erasure</strong> — Request deletion of your personal data</li>
                <li><strong className="text-stone-700 dark:text-stone-300">Right to object</strong> — Object to processing of your data for direct marketing</li>
                <li><strong className="text-stone-700 dark:text-stone-300">Right to opt out (CCPA)</strong> — California residents may opt out of the sale of personal information</li>
                <li><strong className="text-stone-700 dark:text-stone-300">Right to withdraw consent</strong> — Withdraw consent for cookie-based processing at any time</li>
              </ul>
              <p>To exercise any of these rights, please <Link to="/contact" className="text-rose-500 hover:underline">contact us</Link>.</p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-stone-800 dark:text-stone-200 mb-2">8. Children&apos;s Privacy</h2>
              <p>This Site is not directed at children under the age of 13 (or 16 in the EU). We do not knowingly collect personal information from children. If you believe a child has provided us with personal information, please contact us immediately.</p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-stone-800 dark:text-stone-200 mb-2">9. Third-Party Links</h2>
              <p>Our Site may contain links to third-party websites. We are not responsible for the privacy practices of those sites and encourage you to review their privacy policies before providing any personal information.</p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-stone-800 dark:text-stone-200 mb-2">10. Changes to This Policy</h2>
              <p>We may update this Privacy Policy from time to time. We will notify you of any significant changes by updating the &ldquo;Last updated&rdquo; date at the top of this page. Your continued use of the Site after changes constitutes acceptance of the updated policy.</p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-stone-800 dark:text-stone-200 mb-2">11. Contact Us</h2>
              <p>If you have questions, concerns, or requests regarding this Privacy Policy or your personal data, please <Link to="/contact" className="text-rose-500 hover:underline">contact us</Link> via our contact form.</p>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
